import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  CASH_OUT_ENROLLMENT_REWARDS_AMOUNT,
  CASH_OUT_MATRIX_LEG_PER_ENROLLMENT,
} from "../../config/cashOutMatrixConfig";
import { formatCashOutProgramPublicId } from "../../config/cashOutProgramConfig";
import { listCashOutProgramsByCartOrderId } from "./cashOutProgramQueries";
import {
  applyCashOutMatrixEnrollmentLeg,
  initializeCashOutProgramMatrix,
} from "./cashOutMatrixEngine";
import { creditRewardsRetirementWallet } from "./rewardsRetirementWallet";
import {
  buildCashOutTransactionId,
  recordCashOutLedgerEntry,
} from "./cashOutLedger";

async function appendProgramsToUserProfile(userId, programIds) {
  if (!userId || !programIds?.length) return;
  const snap = await getDocs(
    query(collection(db, "users"), where("uid", "==", userId))
  );
  if (snap.empty) return;
  const userRef = doc(db, "users", snap.docs[0].id);
  const existing = snap.docs[0].data().cashOutProgramIds || [];
  const merged = [...new Set([...existing, ...programIds])];
  await updateDoc(userRef, {
    cashOutProgramIds: merged,
    updatedAt: new Date().toISOString(),
  });
}

async function nextCashOutEnrollmentSeq() {
  const counterRef = doc(db, "cashOutMeta", "counters");
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const next = (snap.exists() ? snap.data().programSeq || 0 : 0) + 1;
    tx.set(counterRef, { programSeq: next }, { merge: true });
    return next;
  });
}

/**
 * Create one Cash Out Program per ₹3,000 block (₹300 fee).
 * Fee: ₹150 → COP matrix fill for upline; ₹150 → Rewards & Retirement.
 */
export async function createCashOutProgramsForOrder({
  userId,
  userEmail,
  userName,
  cartOrderId,
  blockCount,
  enrollmentFee,
  orderSubtotal,
}) {
  const count = Number(blockCount) || 0;
  if (count <= 0) {
    return { created: false, programIds: [] };
  }

  const existingForOrder = await listCashOutProgramsByCartOrderId(cartOrderId);
  if (existingForOrder.length > 0) {
    const programIds = existingForOrder.map((p) => p.programId);
    return {
      created: true,
      programIds,
      count: programIds.length,
      matrixFills: existingForOrder.map((p) => p.matrixFill).filter(Boolean),
      idempotent: true,
    };
  }

  const programIds = [];
  const now = new Date().toISOString();
  const matrixFills = [];

  for (let i = 0; i < count; i += 1) {
    const enrollmentSeq = await nextCashOutEnrollmentSeq();
    const programId = formatCashOutProgramPublicId(enrollmentSeq);

    const programRef = await addDoc(collection(db, "cashOutPrograms"), {
      programId,
      enrollmentSeq,
      userId,
      userEmail: userEmail || "",
      userName: userName || "",
      sourceCartOrderId: cartOrderId,
      blockIndex: i + 1,
      blocksInOrder: count,
      orderSubtotalAtEnrollment: Number(orderSubtotal) || 0,
      enrollmentFeePerBlock: 300,
      matrixLegAmount: CASH_OUT_MATRIX_LEG_PER_ENROLLMENT,
      enrollmentRewardsAmount: CASH_OUT_ENROLLMENT_REWARDS_AMOUNT,
      totalEnrollmentFee: Number(enrollmentFee) || count * 300,
      status: "active",
      currentLevel: 1,
      levelPaymentsInCycle: 0,
      createdAt: now,
      updatedAt: now,
      enrolledAt: serverTimestamp(),
    });

    await initializeCashOutProgramMatrix(programRef.id, programId, userId);

    const matrixFill = await applyCashOutMatrixEnrollmentLeg({
      userId,
      programId,
      cartOrderId,
    });
    matrixFills.push(matrixFill);

    const rewardsCredit = await creditRewardsRetirementWallet(
      CASH_OUT_ENROLLMENT_REWARDS_AMOUNT,
      {
        programId,
        userId,
        reference: cartOrderId,
        metadata: { kind: "COP_ENROLLMENT_FEE" },
      }
    );

    await recordCashOutLedgerEntry({
      transactionId: buildCashOutTransactionId(),
      programId,
      userId,
      amount: 300,
      transactionType: "PROGRAM_ENROLLED",
      fromWallet: "CART_CHECKOUT",
      toWallet: "COP_PROGRAM",
      reference: cartOrderId,
      metadata: {
        matrixFill,
        rewardsRetirementAmount: CASH_OUT_ENROLLMENT_REWARDS_AMOUNT,
        matrixLegAmount: CASH_OUT_MATRIX_LEG_PER_ENROLLMENT,
      },
    });

    await updateDoc(programRef, {
      matrixFill,
      rewardsRetirementCredited: rewardsCredit.credited === true,
      updatedAt: new Date().toISOString(),
    });

    programIds.push(programId);
  }

  await appendProgramsToUserProfile(userId, programIds);

  return { created: true, programIds, count, matrixFills };
}

export default { createCashOutProgramsForOrder };
